import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { usePoTemplate } from '@/hooks/usePurchaseOrders'
import { PoCard } from './PurchaseOrderForm'

/**
 * Terms & conditions for one order (z8nrz7f7dz). By default the order prints
 * the template's standard terms; switching to custom copies them in as a
 * starting point to edit for this order only. Switching back drops the custom
 * text, and the standard terms print again.
 */
export function TermsCard({ terms, onChange }: { terms: string | null; onChange: (terms: string | null) => void }) {
  const standard = usePoTemplate().data?.layout.textBlocks.terms ?? ''
  const custom = terms !== null

  return (
    <PoCard
      title='Terms & conditions'
      action={
        <div className='flex items-center gap-2'>
          <Label htmlFor='po-terms-custom' className='text-xs font-medium text-slate-600'>
            Custom terms for this order
          </Label>
          <Switch id='po-terms-custom' checked={custom} onCheckedChange={(on) => onChange(on ? standard : null)} />
        </div>
      }
    >
      {custom ? (
        <div className='space-y-1.5'>
          <Label htmlFor='po-terms' className='sr-only'>
            Terms & conditions
          </Label>
          <Textarea
            id='po-terms'
            rows={10}
            className='font-mono text-xs leading-relaxed'
            value={terms}
            onChange={(e) => onChange(e.target.value)}
          />
          <p className='text-[11px] text-slate-500'>
            Prints on this order only. Placeholders like {'{{company.legalName}}'} and **bold** work as in the template.
          </p>
        </div>
      ) : standard.trim() ? (
        <div className='space-y-1.5'>
          <p className='max-h-32 overflow-y-auto whitespace-pre-wrap rounded-md bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-600'>
            {standard}
          </p>
          <p className='text-[11px] text-slate-500'>
            Your standard terms from the PO template. Edit them for every order under Configure → Purchase orders → Text.
          </p>
        </div>
      ) : (
        <p className='text-sm text-slate-500'>
          No standard terms yet. Add them under Configure → Purchase orders → Text, or switch on custom terms.
        </p>
      )}
    </PoCard>
  )
}
